import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import StatusPill from '../../components/StatusPill';
import { Card, Field, LoadingRows, Notice, PageHeader, Segmented, Toggle } from '../../components/ui/Page';
import { countryName } from '../../lib/countries';
import { ago } from '../../lib/format';

type Settings = {
  gradeScale: 'LETTER' | 'MICUS';
  strictness: 'LENIENT' | 'BALANCED' | 'STRICT';
  weights: { financial: number; business: number; governance: number; documentation: number };
  partnerReadyScore: number;
  temperature: number;
  includeDocuments: boolean;
  includeMethodology: boolean;
};

type Provider = { id: string; label: string; model: string; description: string | null; isDefault: boolean };

type PromptVersion = {
  id: string;
  version: number;
  status: 'DRAFT' | 'CANARY' | 'ACTIVE' | 'ARCHIVED';
  compiledHash: string | null;
  changelog: string | null;
  createdAt: string;
  createdBy: { name: string | null; email: string } | null;
};

type PromptFamily = {
  id: string;
  key: string;
  canaryPercent: number;
  activeVersionId: string | null;
  canaryVersionId: string | null;
  draftVersionId: string | null;
  versions: PromptVersion[];
};

type RatingDetail = {
  countryCode: string;
  countryName: string;
  prompt: {
    id: string;
    systemPrompt: string;
    guidelines: string | null;
    settings: Settings;
    sampleInput: string | null;
    lastTestOutput: string | null;
    lastTestedAt: string | null;
    providerId: string | null;
    version: number;
    isActive: boolean;
    updatedAt: string;
  } | null;
  family: PromptFamily | null;
  providers: Provider[];
  defaultModel: string;
  documents: { id: string; code: string; title: string; category: string | null; required: boolean; tracks: string[]; aiGuidance: string | null }[];
  compiled: string;
  defaults: { systemPrompt: string; settings: Settings };
};

type Draft = { systemPrompt: string; guidelines: string; settings: Settings; providerId: string; isActive: boolean; sampleInput: string };

const WEIGHTS: { key: keyof Settings['weights']; label: string; hint: string }[] = [
  { key: 'financial', label: 'Financial strength', hint: 'Revenue, margins, leverage, cash flow' },
  { key: 'business', label: 'Business & market', hint: 'Model, customers, concentration, sector' },
  { key: 'governance', label: 'Governance & legal', hint: 'Ownership, compliance, litigation' },
  { key: 'documentation', label: 'Documentation quality', hint: 'Completeness and verification of the pack' },
];

const CATEGORY_LABEL: Record<string, string> = { FINANCIAL: 'Financial', LEGAL: 'Ownership & legal', KYC: 'Individuals & KYC', BUSINESS: 'Business' };

function Slider({ value, onChange, min, max, step = 1, suffix = '' }: { value: number; onChange: (v: number) => void; min: number; max: number; step?: number; suffix?: string }) {
  return (
    <div className="flex items-center gap-3">
      <input type="range" className="mz-range flex-1" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="w-12 text-right text-[13px] font-semibold tabular-nums text-[#3b0a22]">
        {value}
        {suffix}
      </span>
    </div>
  );
}

export default function RatingFormPage() {
  const { country = '' } = useParams();
  const code = country.toUpperCase();
  const { data, loading, error: loadError, setData } = useApi<RatingDetail>(`/admin/ratings/${code}`);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState<string>('');
  const [compiled, setCompiled] = useState<{ text: string; documentCount: number } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<{ output: string; model: string; latencyMs?: number; at: string | null } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const previewSeq = useRef(0);
  const name = data?.countryName || countryName(code);

  useEffect(() => {
    if (!data || draft) return;
    const p = data.prompt;
    const next: Draft = {
      systemPrompt: p?.systemPrompt || data.defaults.systemPrompt,
      guidelines: p?.guidelines || '',
      settings: p?.settings || data.defaults.settings,
      providerId: p?.providerId || '',
      isActive: p?.isActive ?? true,
      sampleInput: p?.sampleInput || '',
    };
    setDraft(next);
    setSaved(p ? JSON.stringify(next) : '');
    setCompiled({ text: data.compiled, documentCount: data.documents.length });
    if (p?.lastTestOutput) setTest({ output: p.lastTestOutput, model: '', at: p.lastTestedAt });
  }, [data, draft]);

  const dirty = draft !== null && JSON.stringify(draft) !== saved;

  useEffect(() => {
    if (!draft || !data) return;
    if (draft.systemPrompt.trim().length < 40) return;
    const id = ++previewSeq.current;
    const t = window.setTimeout(async () => {
      setPreviewing(true);
      try {
        const { data: res } = await api.post(`/admin/ratings/${code}/preview`, {
          countryName: name,
          systemPrompt: draft.systemPrompt,
          guidelines: draft.guidelines || null,
          settings: draft.settings,
        });
        if (id === previewSeq.current) setCompiled({ text: res.compiled, documentCount: res.documentCount });
      } catch {
        /* keep the last good preview */
      } finally {
        if (id === previewSeq.current) setPreviewing(false);
      }
    }, 600);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.systemPrompt, draft?.guidelines, draft?.settings, code, name]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => d && { ...d, [key]: value });
  const setSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => setDraft((d) => d && { ...d, settings: { ...d.settings, [key]: value } });
  const setWeight = (key: keyof Settings['weights'], value: number) =>
    setDraft((d) => d && { ...d, settings: { ...d.settings, weights: { ...d.settings.weights, [key]: value } } });

  const weightTotal = draft ? Object.values(draft.settings.weights).reduce((a, b) => a + b, 0) : 0;

  const docGroups = useMemo(() => {
    const docs = data?.documents || [];
    const map = new Map<string, number>();
    docs.forEach((d) => map.set(d.category || 'OTHER', (map.get(d.category || 'OTHER') || 0) + 1));
    return Array.from(map.entries());
  }, [data]);
  const missingGuidance = (data?.documents || []).filter((d) => !d.aiGuidance);

  const save = async (quiet = false) => {
    if (!draft) return false;
    setSaving(true);
    if (!quiet) setNotice(null);
    try {
      const { data: res } = await api.put(`/admin/ratings/${code}`, {
        countryName: name,
        systemPrompt: draft.systemPrompt,
        guidelines: draft.guidelines || null,
        settings: draft.settings,
        providerId: draft.providerId || null,
        isActive: draft.isActive,
        sampleInput: draft.sampleInput || null,
      });
      setSaved(JSON.stringify(draft));
      setData((prev) => prev && { ...prev, prompt: res.prompt, family: res.family || prev.family });
      const snap = res.snapshot as { version: number; created: boolean; status: string } | undefined;
      if (!quiet) {
        const live = snap?.status === 'ACTIVE';
        setNotice({
          tone: 'success',
          text: live
            ? `Released as v${snap?.version}. New ratings in ${name} use this prompt.`
            : `Saved as draft v${snap?.version || res.prompt.version}. Promote it to send this prompt to new ratings.`,
        });
      }
      return true;
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not save the prompt.') });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!draft) return;
    setNotice(null);
    if ((dirty || !data?.prompt) && !(await save(true))) return;
    setTesting(true);
    try {
      const { data: res } = await api.post(`/admin/ratings/${code}/test`, { sampleInput: draft.sampleInput });
      setTest({ output: res.output, model: res.model, latencyMs: res.latencyMs, at: new Date().toISOString() });
      setData((prev) => prev && { ...prev, prompt: res.prompt, family: res.family || prev.family });
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'The test run failed.') });
    } finally {
      setTesting(false);
    }
  };

  const copy = async () => {
    if (!compiled) return;
    await navigator.clipboard.writeText(compiled.text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const refreshFamily = (family: PromptFamily) => setData((prev) => prev && { ...prev, family });

  const release = async (versionId: string, target: 'ACTIVE' | 'CANARY') => {
    setReleasing(true);
    setNotice(null);
    try {
      const { data: res } = await api.post(`/admin/ratings/${code}/versions/${versionId}/promote`, {
        target,
        canaryPercent: target === 'CANARY' ? data?.family?.canaryPercent || 10 : undefined,
      });
      refreshFamily(res.family);
      setNotice({
        tone: 'success',
        text: target === 'ACTIVE' ? 'This version is now live for new ratings.' : 'Canary started — a slice of companies will use this version.',
      });
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not promote this version.') });
    } finally {
      setReleasing(false);
    }
  };

  const rollback = async (versionId?: string) => {
    if (!window.confirm('Restore the previous live prompt for new ratings? In-flight drafts keep the version they were generated with.')) return;
    setReleasing(true);
    setNotice(null);
    try {
      const { data: res } = await api.post(`/admin/ratings/${code}/rollback`, versionId ? { versionId } : {});
      refreshFamily(res.family);
      setNotice({ tone: 'success', text: 'Rolled back. New ratings use the restored version.' });
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not roll back.') });
    } finally {
      setReleasing(false);
    }
  };

  if (loading && !data) return <LoadingRows rows={8} />;
  if (!data || !draft) return <Notice tone="error">{loadError || 'Could not load this rating prompt.'}</Notice>;

  const providerOptions = [
    { value: '', label: 'Use the default model', description: `Currently ${data.defaultModel}`, badge: 'Default' },
    ...data.providers.map((p) => ({ value: p.id, label: p.label, description: p.description || p.model, badge: p.isDefault ? 'Default' : undefined, keywords: p.model })),
  ];

  return (
    <div className="pb-4">
      <PageHeader
        crumbs={[{ label: 'Ratings', to: '/ratings' }, { label: name }]}
        title={`${name} rating prompt`}
        description={
          data.prompt
            ? `Working copy v${data.prompt.version} · updated ${ago(data.prompt.updatedAt)}. Live ratings follow the promoted version, not this editor until you promote.`
            : `No custom prompt yet — ${name} is rated with the Micus default. Save to create a draft version.`
        }
        actions={
          <>
            {data.prompt ? (
              data.prompt.isActive ? <StatusPill label="Active" tone="green" /> : <StatusPill label="Paused" tone="slate" />
            ) : (
              <StatusPill label="Default" tone="amber" />
            )}
            <Link to={`/documents/${code}`} className="mz-btn-ghost">
              Edit documents
            </Link>
          </>
        }
      />

      {notice && (
        <Notice tone={notice.tone} onClose={() => setNotice(null)} className="mb-4">
          {notice.text}
        </Notice>
      )}

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card title="Model">
            <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
              <Field label="AI model" hint={data.providers.length ? 'Manage models in AI Settings.' : undefined}>
                <Select value={draft.providerId} onChange={(v) => set('providerId', v)} options={providerOptions} />
              </Field>
              <div className="flex items-center gap-2 pb-1.5">
                <Toggle checked={draft.isActive} onChange={(v) => set('isActive', v)} label="Prompt active" />
                <span className="text-[13px] font-medium text-[#7a1f4a]">{draft.isActive ? 'Active' : 'Paused — default used'}</span>
              </div>
            </div>
          </Card>

          <Card
            title="System prompt"
            subtitle="The analyst persona, method and rules. Applies before the country guidelines and documents."
            actions={
              <button
                type="button"
                className="text-xs font-semibold text-[#d6006e] hover:underline"
                onClick={() => window.confirm('Replace the system prompt with the Micus default?') && set('systemPrompt', data.defaults.systemPrompt)}
              >
                Reset to default
              </button>
            }
          >
            <textarea rows={14} className="mz-input font-mono text-[12.5px] leading-relaxed" value={draft.systemPrompt} onChange={(e) => set('systemPrompt', e.target.value)} />
            <p className={`mz-hint ${draft.systemPrompt.trim().length < 40 ? '!text-[#be123c]' : ''}`}>
              {draft.systemPrompt.length.toLocaleString()} characters{draft.systemPrompt.trim().length < 40 ? ' — at least 40 required' : ''}
            </p>
          </Card>

          <Card title={`${name} guidelines`} subtitle="Local regulation, accounting standards, lender norms and red flags specific to this country.">
            <textarea
              rows={10}
              className="mz-input text-[13px] leading-relaxed"
              value={draft.guidelines}
              onChange={(e) => set('guidelines', e.target.value)}
              placeholder={`e.g. Financial statements follow local GAAP. Check the tax registration matches the company name. Treat unpaid statutory dues as a governance risk…`}
            />
          </Card>

          <Card title="Scoring settings">
            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Grade scale">
                  <Segmented
                    value={draft.settings.gradeScale}
                    onChange={(v) => setSetting('gradeScale', v)}
                    options={[
                      { value: 'LETTER', label: 'A+ … D' },
                      { value: 'MICUS', label: 'M-AAA … M-D' },
                    ]}
                  />
                </Field>
                <Field label="Evidence strictness">
                  <Segmented
                    value={draft.settings.strictness}
                    onChange={(v) => setSetting('strictness', v)}
                    options={[
                      { value: 'LENIENT', label: 'Lenient' },
                      { value: 'BALANCED', label: 'Balanced' },
                      { value: 'STRICT', label: 'Strict' },
                    ]}
                  />
                </Field>
              </div>

              <div>
                <div className="mb-2 flex items-baseline justify-between">
                  <p className="mz-label !mb-0">Score weights</p>
                  <p className={`text-xs ${weightTotal === 100 ? 'text-[#a77d92]' : 'font-semibold text-[#b45309]'}`}>
                    Total {weightTotal}%{weightTotal === 100 ? '' : ' — normalised to 100% when compiled'}
                  </p>
                </div>
                <div className="space-y-3">
                  {WEIGHTS.map((w) => (
                    <div key={w.key}>
                      <div className="flex items-baseline justify-between text-[13px]">
                        <span className="font-medium text-[#3b0a22]">{w.label}</span>
                        <span className="text-xs text-[#a77d92]">{w.hint}</span>
                      </div>
                      <Slider value={draft.settings.weights[w.key]} onChange={(v) => setWeight(w.key, v)} min={0} max={100} step={5} suffix="%" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Partner-ready score" hint="Below this score the company is flagged as not ready for partners.">
                  <Slider value={draft.settings.partnerReadyScore} onChange={(v) => setSetting('partnerReadyScore', v)} min={0} max={100} step={5} />
                </Field>
                <Field label="Temperature" hint="Lower is more consistent between runs. 0.1–0.3 recommended.">
                  <Slider value={draft.settings.temperature} onChange={(v) => setSetting('temperature', v)} min={0} max={1} step={0.05} />
                </Field>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex items-start gap-3 rounded-lg border border-[#f6dbe7] p-3">
                  <Toggle checked={draft.settings.includeDocuments} onChange={(v) => setSetting('includeDocuments', v)} label="Include document checklist" />
                  <span>
                    <span className="block text-[13px] font-semibold">Include document checklist</span>
                    <span className="block text-xs text-[#a77d92]">Every active {name} document with its AI check.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-[#f6dbe7] p-3">
                  <Toggle checked={draft.settings.includeMethodology} onChange={(v) => setSetting('includeMethodology', v)} label="Include track methodology" />
                  <span>
                    <span className="block text-[13px] font-semibold">Include track methodology</span>
                    <span className="block text-xs text-[#a77d92]">Corporate, partnership and proprietorship rules.</span>
                  </span>
                </label>
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-4 xl:sticky xl:top-4">
          <VersionLedger family={data.family} busy={releasing} onPromote={release} onRollback={rollback} />
          <Card
            title="Document checklist"
            subtitle={`${data.documents.length} active document${data.documents.length === 1 ? '' : 's'} from Documents · ${data.documents.filter((d) => d.required).length} required`}
            actions={
              <Link to={`/documents/${code}`} className="text-xs font-semibold text-[#d6006e] hover:underline">
                Manage →
              </Link>
            }
          >
            {data.documents.length === 0 ? (
              <Notice tone="warning">
                {name} has no documents yet.{' '}
                <Link to={`/documents/${code}`} className="font-semibold underline">
                  Add documents
                </Link>{' '}
                so the AI knows what to expect.
              </Notice>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {docGroups.map(([cat, n]) => (
                    <span key={cat} className="rounded-full bg-[#fdf2f8] px-2.5 py-1 text-xs font-medium text-[#9d0050]">
                      {CATEGORY_LABEL[cat] || 'Other'} · {n}
                    </span>
                  ))}
                </div>
                {missingGuidance.length > 0 && (
                  <p className="mt-3 text-xs text-[#b45309]">
                    {missingGuidance.length} document{missingGuidance.length === 1 ? ' has' : 's have'} no AI check:{' '}
                    {missingGuidance.slice(0, 3).map((d, i) => (
                      <span key={d.id}>
                        {i > 0 && ', '}
                        <Link to={`/documents/${code}/${d.id}`} className="font-semibold underline">
                          {d.title}
                        </Link>
                      </span>
                    ))}
                    {missingGuidance.length > 3 && ` and ${missingGuidance.length - 3} more`}
                  </p>
                )}
                {!draft.settings.includeDocuments && <p className="mt-3 text-xs text-[#b45309]">The checklist is switched off in scoring settings and won't be sent to the AI.</p>}
              </>
            )}
          </Card>

          <Card
            title="Compiled prompt"
            subtitle={previewing ? 'Updating…' : `${(compiled?.text.length || 0).toLocaleString()} characters · what the rating model receives`}
            actions={
              <button type="button" className="text-xs font-semibold text-[#d6006e] hover:underline" onClick={() => void copy()}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            }
            padded={false}
          >
            <pre className={`max-h-[420px] overflow-auto whitespace-pre-wrap break-words bg-[#fdfafb] p-4 font-mono text-[11.5px] leading-relaxed text-[#4a1530] transition-opacity ${previewing ? 'opacity-60' : ''}`}>
              {compiled?.text}
            </pre>
          </Card>

          <Card title="Test with a sample company" subtitle="Runs the saved prompt through the AI platform. Unsaved changes are saved first as a draft version.">
            <textarea
              rows={6}
              className="mz-input text-[13px]"
              value={draft.sampleInput}
              onChange={(e) => set('sampleInput', e.target.value)}
              placeholder="Company: Acme Pvt Ltd, manufacturing, FY24 revenue ₹12 Cr, EBITDA 14%, debt/equity 0.8… Documents: audited financials FY22–24 (verified), GST returns (verified), bank statements (missing)…"
            />
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-xs text-[#a77d92]">{test?.at ? `Last run ${ago(test.at)}` : 'Not tested yet'}</span>
              <button type="button" className="mz-btn-primary" disabled={testing || saving || draft.sampleInput.trim().length < 10} onClick={() => void runTest()}>
                {testing ? 'Rating…' : 'Run test'}
              </button>
            </div>
            {test && (
              <div className="mt-3">
                {test.model && (
                  <p className="mb-1.5 text-xs text-[#a77d92]">
                    {test.model}
                    {test.latencyMs !== undefined ? ` · ${(test.latencyMs / 1000).toFixed(1)}s` : ''}
                  </p>
                )}
                <pre className="max-h-[360px] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-[#f6dbe7] bg-white p-3 text-[12.5px] leading-relaxed text-[#3b0a22]">{test.output}</pre>
              </div>
            )}
          </Card>
        </div>
      </div>

      <div className="mz-card sticky bottom-3 mt-4 flex flex-wrap items-center justify-end gap-3 px-4 py-3">
        <span className="mr-auto text-xs text-[#a77d92]">{dirty ? 'Unsaved changes — save creates a draft version' : data.prompt ? 'Working copy saved' : 'Using the Micus default until saved'}</span>
        <button type="button" className="mz-btn-primary" disabled={saving || (!dirty && Boolean(data.prompt)) || draft.systemPrompt.trim().length < 40} onClick={() => void save()}>
          {saving ? 'Saving…' : data.prompt ? 'Save draft' : 'Create prompt'}
        </button>
      </div>
    </div>
  );
}

const STATUS_TONE: Record<PromptVersion['status'], 'green' | 'amber' | 'slate' | 'pink'> = {
  ACTIVE: 'green',
  CANARY: 'amber',
  DRAFT: 'pink',
  ARCHIVED: 'slate',
};

function VersionLedger({
  family,
  busy,
  onPromote,
  onRollback,
}: {
  family: PromptFamily | null;
  busy: boolean;
  onPromote: (id: string, target: 'ACTIVE' | 'CANARY') => void;
  onRollback: (id?: string) => void;
}) {
  const versions = family?.versions || [];
  const live = versions.find((v) => v.id === family?.activeVersionId);
  const canary = versions.find((v) => v.id === family?.canaryVersionId);
  return (
    <Card
      title="Release"
      subtitle={
        live
          ? `Live v${live.version}${canary ? ` · canary v${canary.version} at ${family?.canaryPercent}%` : ''}`
          : 'Save a draft, then promote it. New ratings follow the live pointer, not the editor.'
      }
    >
      {versions.length === 0 ? (
        <p className="text-xs text-[#a77d92]">No versions yet. Saving creates v1 and releases it.</p>
      ) : (
        <ul className="space-y-2">
          {versions.slice(0, 12).map((v) => {
            const isCanary = v.id === family?.canaryVersionId;
            return (
              <li key={v.id} className="rounded-lg border border-[#f6dbe7] px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#3b0a22]">v{v.version}</span>
                  <StatusPill label={v.status} tone={STATUS_TONE[v.status]} />
                  <span className="text-[11px] text-[#a77d92]">{ago(v.createdAt)}</span>
                  <span className="ml-auto flex gap-2">
                    {v.status !== 'ACTIVE' && (
                      <button type="button" className="text-[11px] font-semibold text-[#d6006e] hover:underline disabled:opacity-40" disabled={busy} onClick={() => onPromote(v.id, 'ACTIVE')}>
                        Promote
                      </button>
                    )}
                    {v.status === 'DRAFT' && !isCanary && (
                      <button type="button" className="text-[11px] font-semibold text-[#b45309] hover:underline disabled:opacity-40" disabled={busy} onClick={() => onPromote(v.id, 'CANARY')}>
                        Canary 10%
                      </button>
                    )}
                    {v.status === 'ARCHIVED' && (
                      <button type="button" className="text-[11px] font-semibold text-[#7a1f4a] hover:underline disabled:opacity-40" disabled={busy} onClick={() => onRollback(v.id)}>
                        Restore
                      </button>
                    )}
                  </span>
                </div>
                {v.changelog && <p className="mt-1 text-[11px] text-[#9a6b82]">{v.changelog}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {live && versions.some((v) => v.status === 'ARCHIVED') && (
        <button type="button" className="mt-3 text-xs font-semibold text-[#7a1f4a] hover:underline disabled:opacity-40" disabled={busy} onClick={() => onRollback()}>
          Roll back to previous live version
        </button>
      )}
    </Card>
  );
}
